import React, {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {Button, Input} from "antd";
import {getCurrentUserId} from "../utils/userUtils";

const API_BASE_URL = 'https://plansync-backend-234288438538.us-central1.run.app';
const userId = getCurrentUserId();

const DocumentPages = ({selectedDocument, currentPage}) => {
    const [page, setPage] = useState(1);
    const [inputValue, setInputValue] = useState('1');
    const [pageCount, setPageCount] = useState(0)

    const getPageCount = useCallback(async (fileName) => {
        let pageCount = 0;
        await axios
            .get(`${API_BASE_URL}/api/getPageCount`, {
                params: {userId, fileName},
            })
            .then((response) => {
                pageCount = response?.data?.total_pages;
            })
            .catch((error) => {
                console.error('getPageCount error', error);
            });
        return pageCount;
    }, []);

    useEffect(() => {
        if (selectedDocument) {
            getPageCount(selectedDocument.name)
                .then((pageCount) => {
                    setPageCount(pageCount);
                });
        }
    }, [selectedDocument, getPageCount]);

    useEffect(() => {
        setPage(currentPage);
        setInputValue(currentPage.toString());
    }, [currentPage]);

    // Update to ensure currentPage is treated as a number
    useEffect(() => {
        // Force conversion to number to prevent string/number comparison issues
        const pageNumber = Number(currentPage);
        setPage(pageNumber);
        setInputValue(pageNumber.toString());
    }, [currentPage]);

    const handlePrevPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    }

    const handleNextPage = () => {
        if (page < pageCount) {
            setPage(page + 1);
        }
    }

    const handleInputChange = (value) => {
        if (value === '' || /^\d+$/.test(value)) {
            setInputValue(value);
        }
    };

    const handlePageSubmit = () => {
        let newPage = parseInt(inputValue) || 1;
        
        newPage = Math.min(Math.max(newPage, 1), pageCount);
        
        setPage(newPage);
        setInputValue(newPage.toString());
    };

    useEffect(() => {
        setInputValue(page.toString());
    }, [page]);

    // Properly encode URL parameters for image data
    const getImageDataUri = (fileName, pageNumber) => {
        // Properly encode each parameter
        const encodedFileName = encodeURIComponent(fileName);
        const encodedUserId = encodeURIComponent(userId);
        
        // Construct the full URL with query parameters
        return `${API_BASE_URL}/api/getImageData?userId=${encodedUserId}&fileName=${encodedFileName}&pageNumber=${pageNumber}`;
    }

    return (
        <div className="flex flex-col h-full relative">
            <div 
                className="h-full overflow-auto"
                style={{
                    position: 'relative',
                    minHeight: '400px'  // minimum height to ensure proper scrolling
                }}
            >
                <img
                    className="w-full"
                    id={`page-${page}`}
                    src={getImageDataUri(selectedDocument.name, page)}
                    alt={`Page ${page}`}
                />
                
                <div 
                    className="flex justify-center items-center space-x-4 py-4 bg-gray-50 rounded-lg shadow-sm"
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'rgba(249, 250, 251, 0.95)',
                        backdropFilter: 'blur(4px)',
                        marginTop: 'auto'
                    }}
                >
                    <Button
                        type="default"
                        onClick={handlePrevPage}
                        className="hover:bg-gray-100 transition-colors"
                        style={{ 
                            border: 'none', 
                            boxShadow: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    </Button>
                    <div className="flex items-center space-x-3">
                        <Input
                            type="text"
                            value={inputValue}
                            className="w-16 text-center rounded-md"
                            style={{
                                border: '1px solid #e5e7eb',
                                boxShadow: 'none'
                            }}
                            onChange={(e) => handleInputChange(e.target.value)}
                            onPressEnter={handlePageSubmit}
                            onBlur={handlePageSubmit}
                        />
                        <span className="text-gray-500">of</span>
                        <span className="font-medium">{pageCount}</span>
                    </div>
                    <Button
                        type="default"
                        onClick={handleNextPage}
                        className="hover:bg-gray-100 transition-colors"
                        style={{ 
                            border: 'none', 
                            boxShadow: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default DocumentPages;